import Parse from "parse"
import { parseLimitRequest } from "../../utils"
import { COLLECTION_NAMES, convertIdToPointer } from "../../parseUtils"

export const ReusableProductionStep = Parse.Object.extend("ReusableProductionStep")

export const getReusableProductionSteps = async (include = [
	"productionSteps.kitchenArea",
	"productionSteps.machineType",
	"productionSteps.stepComponents.supplierItem",
	"productionSteps.stepComponents.transformationMode",
	"productionSteps.stepComponents.transformRate"
], options = {}) => {
	try {
		const reusableProductionsStepsQuery = new Parse.Query(ReusableProductionStep)
			.notEqualTo("deleted", true)
			.descending("updatedAt")
			.include(include)
			.limit(parseLimitRequest)
		if (options.returnQuery) {
			return reusableProductionsStepsQuery
		}
		const reusableProductionsSteps = await reusableProductionsStepsQuery.find() || []

		return reusableProductionsSteps.map((step) => step.toJSON())
	}
	catch (e) {
		console.error("parseManager.recipe.parseReusableProductionStepsManager.getReusableProductionSteps error : ", e)
		return Promise.reject(e)
	}
}

/*
* same as getReusableProductionSteps but with a pipeline to limit the data retrieved
* x4 faster and less memory consuming
*/
export const getReusableProductionStepsPipeline = async (noIncludes = false) => {
	const { data: reusableProductionsSteps } = await Parse.Cloud.run("aggregates", {
		table: "ReusableProductionStep",
		pipeline: [
			{ match: { deleted: { $ne: true } } },
			{ sort: { _updated_at: -1 } },
			{
				project: {
					_id: 1,
					objectId: 1,
					id: 1,
					name: 1,
					updatedAt: 1,
					_updated_at: 1,
					...noIncludes ? {} : {
						ingredients: 1,
						transformations: 1,
					}
				}
			}
		]
	})
	if (noIncludes) return reusableProductionsSteps

	const sets = reusableProductionsSteps.reduce((ids, step) => {
		(step.ingredients || []).forEach((sc) => {
			if (sc.supplierItem) {
				ids.supplierItemsIds.add(sc.supplierItem.id)
			}
		});
		(step.transformations || []).forEach((tm) => {
			if (tm.transformationMode) {
				ids.transformationModesIds.add(tm.transformationMode.id)
			}
		})
		return ids
	}, {
		supplierItemsIds: new Set(),
		transformationModesIds: new Set()
	})

	const [supplierItemResponse, transformationModesResponse] = await Promise.all([
		Parse.Cloud.run("aggregates", {
			table: "SupplierItems",
			pipeline: [
				{ match: { _id: { $in: Array.from(sets.supplierItemsIds) } } },
				{ project: { isActive: 1, deleted: 1, name: 1 } },
			]
		}),
		Parse.Cloud.run("aggregates", {
			table: "TransformationModes",
			pipeline: [
				{ match: { _id: { $in: Array.from(sets.transformationModesIds) } } },
				{ project: { name: 1 } }
			]
		})
	])

	const { data: supplierItem } = supplierItemResponse
	const { data: transformationModes } = transformationModesResponse

	//remerge data here
	const mergedData = reusableProductionsSteps.map((step) => ({
		...step,
		ingredients: (step.ingredients || []).map((ig) => {
			return {
				...ig,
				previous: ig.supplierItem,
				supplierItem: supplierItem.find((si) => si._id === ig.supplierItem?.id)
			}
		}),
		transformations: (step.transformations || []).map((tm) => {
			return {
				...tm,
				transformationMode: transformationModes.find((trm) => trm._id === tm.transformationMode?.id)
			}
		})
	}))
	return mergedData
}

export const getReusableProductionStep = async (id, toJson = true) => {
	try {
		const reusableProductionStep = await new Parse.Query(ReusableProductionStep)
			.equalTo("objectId", id)
			.first()

		if (!reusableProductionStep) return null

		const reusableProductionStepJson = reusableProductionStep.toJSON()
		const productionSteps = []
		// for some unknown and strange cache (?) issue, we should query separately the production step
		for (const productionStep of reusableProductionStep.get("productionSteps")) {
			const parseProductionStep = await new Parse.Query("ProductionStep")
				.equalTo("objectId", productionStep.id)
				.include([
					"kitchenArea",
					"machineType",
					"stepComponents.supplierItem",
					"stepComponents.transformationMode",
					"stepComponents.priorSteps.stepComponents.transformationMode",
					"stepComponents.priorSteps.stepComponents.supplierItem",
				])
				.first()

			productionSteps.push(parseProductionStep.toJSON())
		}

		return toJson
			? { ...reusableProductionStepJson, productionSteps: productionSteps }
			: reusableProductionStep
	}
	catch (e) {
		console.error("parseManager.recipe.parseReusableProductionStepsManager.getReusableStep error : ", e)
		return Promise.reject(e)
	}
}

export const getReusableProductionStepsByStepIdsForMerge = async (productionStepsIds) => {
	try {
		const reusableProductionSteps = []
		const stepsByBaseMap = new Map()
		const productionStepsIdsByNonBaseMap = new Map()
		const productionStepsIdByBaseSourceReusableStepMap = new Map()
		await new Parse.Query(ReusableProductionStep)
			.containedIn("productionSteps.objectId", productionStepsIds)
			.include(["baseReusableProductionStep"])
			.each(reusableStepParseObj => {
				const reusableStep = reusableStepParseObj.toJSON()
				const stepProductionStepIds = reusableStep.productionSteps.map(sps => sps.objectId)
				const count = productionStepsIds.filter(id => stepProductionStepIds.includes(id)).length
				const moreThanOne = count > 1

				if (moreThanOne) {
					const options = { ...reusableStep, optionKey: reusableStep.objectId }
					// Steps that have a baseReusableProductionStep set
					if (reusableStep.baseReusableProductionStep) {
						const baseStepId = reusableStep.baseReusableProductionStep.objectId
						// Display the base step name instead of the reusable step name if there is one
						options.name = reusableStep.baseReusableProductionStep.name
						// Use this to unify the reusable steps sharing the same baseReusableProductionStep
						options.optionKey = baseStepId
						// Group reusable steps by their baseReusableProductionStep
						// to be able to add all of them when one is selected
						const prev = stepsByBaseMap.get(baseStepId) || []
						const currentReusableStep = { reusableStepId: reusableStep.objectId, productionStepIds: reusableStep.productionSteps.map(ps => ps.objectId) }
						stepsByBaseMap.set(baseStepId, [...prev, currentReusableStep])
					// Other steps
					} else {
						productionStepsIdsByNonBaseMap.set(reusableStep.objectId, reusableStep.productionSteps.map(ps => ps.objectId))
					}
					reusableProductionSteps.push(options)
				}
			})

		for (const [reusableId] of stepsByBaseMap.entries()) {
			const productionStepIds = productionStepsIdsByNonBaseMap.get(reusableId) || []
			// If normal steps are used as a base for other steps
			if (productionStepIds.length > 0) {
				// We need the production step ids for later when the merge begin
				productionStepsIdByBaseSourceReusableStepMap.set(reusableId, productionStepIds)
			}
		}

		return { reusableProductionSteps, stepsByBaseMap, productionStepsIdByBaseSourceReusableStepMap }
	}
	catch (e) {
		console.error("parseManager.recipe.parseReusableProductionStepsManager.getReusableProductionStepByIdProductionStepList error : ", e)
		return Promise.reject(e)
	}
}

export const isReusableStepInRecipes = async (reusableStepId) => {
	try {
		const section = await new Parse.Query(COLLECTION_NAMES.section)
			.equalTo("productionSteps.step", convertIdToPointer(COLLECTION_NAMES.reusableProductionStep, reusableStepId))
			.notEqualTo("deleted", true)
			.first()

		if (!section) {
			return false
		}

		const recipe = await new Parse.Query(COLLECTION_NAMES.recipe)
			.equalTo("sections", section)
			.notEqualTo("deleted", true)
			.first()

		return !!recipe
	}
	catch (e) {
		console.error("parseManager.recipe.parseReusableProductionStepsManager.isReusableStepInRecipes error : ", e)
		return Promise.reject(e)
	}
}

export const isReusableStepUsedAsBase = async (reusableStepId) => {
	try {
		const base = await new Parse.Query(COLLECTION_NAMES.reusableProductionStep)
			.equalTo("baseReusableProductionStep", convertIdToPointer(COLLECTION_NAMES.reusableProductionStep, reusableStepId))
			.notEqualTo("deleted", true)
			.first()

		return !!base
	}
	catch (e) {
		console.error("parseManager.recipe.parseReusableProductionStepsManager.isReusableStepUsedAsBase error : ", e)
		return Promise.reject(e)
	}
}
